import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black } from '../../../components/Logo/Logo';
import { API_CONFIG } from '../../../config/api';
import './WindmillGame.css';

// Build absolute URLs for the Windmill game API using the same
// backend base URL as the rest of the app (API_CONFIG.baseURL).
// This ensures the correct path prefix is used in production
// (e.g. /app-store-testing-backend/api/...) instead of assuming
// the API is at the root ( /api/... ).
const getGameApiUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_CONFIG.baseURL}${normalizedPath}`;
};

const WindmillGame = ({ appId: appIdProp, onComplete, parentAppId }) => {
  const { id: idFromParams } = useParams();
  const navigate = useNavigate();
  const id = appIdProp != null ? appIdProp : idFromParams;
  const { user } = useAuth();
  const [app, setApp] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  // Game state
  const [currentStep, setCurrentStep] = useState(0);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [decisionVideo, setDecisionVideo] = useState('decision_win.mp4');
  const [celebrationVideo, setCelebrationVideo] = useState('celebration_win.mp4');

  const videoRef = useRef(null);
  const progressFillRef = useRef(null);
  const questionIdRef = useRef(null);
  const handlingEndRef = useRef(false);
  const fallbackTimerRef = useRef(null);

  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  // Video URLs from app config or defaults
  const getVideoUrl = (filename) => {
    // Use CDN URLs from app config if available
    if (app?.video_urls && app.video_urls.length > 0) {
      const baseUrl = 'https://pathbuddy-media.lon1.cdn.digitaloceanspaces.com/Games/Williams-Windmill/';
      return `${baseUrl}${filename}`;
    }
    // Fallback to relative path
    return `/videos/${filename}`;
  };

  // Game steps (outcome videos come from state so API result can override)
  const steps = [
    { type: 'video', src: getVideoUrl('gameplay.mp4') },
    { type: 'question', id: 'decision_1' },
    { type: 'video', src: getVideoUrl(decisionVideo) },
    { type: 'question', id: 'calculation_1' },
    { type: 'video', src: getVideoUrl(celebrationVideo) }
  ];

  // Debug: log which backend URL the game will call for questions
  useEffect(() => {
    const sampleUrl = getGameApiUrl('/api/windmill-game/question/decision_1');
    console.log('[WINDMILL] Sample question URL (should include /app-store-testing-backend in prod):', sampleUrl);
  }, []);

  // Extra safety net: if, for any reason, the video events
  // (loadstart/canplay/ended) never fire in production, force the
  // game to advance from the intro video to the first question after
  // a timeout. This guarantees that the question endpoint is hit
  // even if the browser/CDN swallows the 'ended' event.
  useEffect(() => {
    const FALLBACK_MS = 60000; // 60 seconds
    const timer = setTimeout(() => {
      if (currentStep === 0 && !showQuestion && !showFeedback) {
        console.warn('[WINDMILL] Global fallback timer fired - forcing handleVideoEnd from step 0');
        handleVideoEnd();
      }
    }, FALLBACK_MS);

    return () => clearTimeout(timer);
  }, [currentStep, showQuestion, showFeedback]);

  // Fetch app data
  useEffect(() => {
    console.log('[WINDMILL] Mounting WindmillGame for app id:', id);
    const fetchApp = async () => {
      try {
        setLoading(true);
        const appData = await appsAPI.getAppById(id);
        setApp(appData);
        console.log('[WINDMILL] App data loaded:', appData?.title, appData?.id);
        
        // Record app opened
        if (id) {
          appsAPI.recordUsage(id, 'app_opened').catch((err) => 
            console.warn('Usage tracking failed:', err)
          );
        }
      } catch (error) {
        console.error('[WINDMILL] Error fetching app:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchApp();
    }
  }, [id]);

  // Load MathJax
  useEffect(() => {
    if (!window.MathJax) {
      window.MathJax = {
        tex: {
          inlineMath: [['$', '$']],
          displayMath: [['$$', '$$']],
          processEscapes: true
        },
        options: {
          skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre']
        }
      };
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-mml-chtml.min.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Video event handlers – only listen for 'ended' when current step is a video step.
  // Otherwise when we transition to a question step, the effect re-runs and re-attaching
  // 'ended' on an already-ended video can fire again and skip the question.
  const isVideoStep = currentStep % 2 === 0 && currentStep <= 4; // steps 0, 2, 4 are video

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      console.log('[WINDMILL] Video element not ready yet for step:', currentStep, 'isVideoStep:', isVideoStep);
      return;
    }

    const handleEnded = () => {
      console.log('[WINDMILL] Video ended event fired at step:', currentStep);
      // Clear any fallback timer if the natural ended event fires
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      handleVideoEnd();
    };

    const handleLoadStart = () => {
      console.log('[WINDMILL] Video loadstart at step:', currentStep, 'isVideoStep:', isVideoStep);
      setVideoLoading(true);
    };

    const handleCanPlay = () => {
      console.log('[WINDMILL] Video canplay at step:', currentStep, 'isVideoStep:', isVideoStep);
      setVideoLoading(false);

      // Fallback: in some environments the 'ended' event may not reliably fire
      // (e.g. streaming/CDN quirks). As a safety net, after a reasonable duration
      // advance to the next step if we are still on the same video step and no
      // question/feedback has appeared.
      if (isVideoStep && !fallbackTimerRef.current) {
        const FALLBACK_MS = 45000; // 45s – longer than expected intro video
        const currentStepSnapshot = currentStep;
        console.log('[WINDMILL] Scheduling video-end fallback for step', currentStepSnapshot, 'in', FALLBACK_MS, 'ms');

        fallbackTimerRef.current = setTimeout(() => {
          // Only trigger if we are still on the same step and nothing else is showing
          if (
            !handlingEndRef.current &&
            currentStepSnapshot === currentStep &&
            !showQuestion &&
            !showFeedback
          ) {
            console.warn('[WINDMILL] Fallback timer fired at step', currentStep, '- forcing handleVideoEnd');
            handleVideoEnd();
          }
          fallbackTimerRef.current = null;
        }, FALLBACK_MS);
      }
    };

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    if (isVideoStep) {
      video.addEventListener('ended', handleEnded);
    }

    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      if (isVideoStep) {
        video.removeEventListener('ended', handleEnded);
      }
    };
  }, [currentStep, isVideoStep, loading]);

  // Update progress bar
  useEffect(() => {
    if (progressFillRef.current) {
      const progress = (currentStep / (steps.length - 1)) * 100;
      progressFillRef.current.style.width = `${progress}%`;
    }
  }, [currentStep]);

  // Render MathJax when question/feedback changes
  useEffect(() => {
    if ((showQuestion || showFeedback) && window.MathJax && window.MathJax.typesetPromise) {
      setTimeout(() => {
        window.MathJax.typesetPromise().catch((err) => console.log('MathJax error:', err));
      }, 100);
    }
  }, [showQuestion, showFeedback, currentQuestion, feedback]);

  const handleVideoEnd = () => {
    console.log('[WINDMILL] handleVideoEnd called at step:', currentStep);
    if (handlingEndRef.current) return;
    handlingEndRef.current = true;

    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);

    if (nextStep < steps.length) {
      const step = steps[nextStep];
      if (step.type === 'question') {
        console.log('[WINDMILL] Moving to question step:', step.id);
        loadQuestion(step.id).finally(() => {
          handlingEndRef.current = false;
        });
      } else {
        console.log('[WINDMILL] Moving to video step with src:', step.src);
        playVideo(step.src);
        handlingEndRef.current = false;
      }
    } else {
      showCompletion();
      handlingEndRef.current = false;
    }
  };

  const playVideo = (src) => {
    console.log('[WINDMILL] playVideo called with src:', src);
    if (videoRef.current) {
      videoRef.current.src = src;
      videoRef.current.load();
      videoRef.current.play();
    }
  };

  const loadQuestion = async (questionId) => {
    try {
      questionIdRef.current = questionId;
      const url = getGameApiUrl(`/api/windmill-game/question/${questionId}`);
      console.log('[WINDMILL] Loading question from:', url, 'for id:', questionId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('[WINDMILL] Question response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[WINDMILL] Question API error:', response.status, errorText);
        throw new Error(`Question failed: ${response.status} - ${errorText}`);
      }
      
      const question = await response.json();
      if (question.error) {
        throw new Error(question.error);
      }
      
      console.log('[WINDMILL] Question loaded successfully:', question.title);
      setCurrentQuestion(question);
      setShowQuestion(true);
    } catch (error) {
      console.error('[WINDMILL] Error loading question:', error);
      setCurrentQuestion({
        title: 'Question unavailable',
        subtitle: 'API Error',
        question: `Could not load question "${questionId}". ${error.message || 'Please check your connection and try again.'}`,
        options: {}
      });
      setShowQuestion(true);
    }
  };

  const selectOption = async (choice) => {
    try {
      const url = getGameApiUrl('/api/windmill-game/answer');
      console.log('[WINDMILL] Submitting answer to:', url, 'choice:', choice, 'question:', questionIdRef.current);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question_id: questionIdRef.current,
          choice: choice,
          user_id: user?.id || 'default'
        })
      });
      
      console.log('[WINDMILL] Answer response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[WINDMILL] Answer API error:', response.status, errorText);
        throw new Error(`Answer submission failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      setFeedback(result);
      setShowQuestion(false);
      setShowFeedback(true);

      if (result.next_video) {
        if (questionIdRef.current === 'decision_1') {
          setDecisionVideo(result.next_video);
        } else if (questionIdRef.current === 'calculation_1') {
          setCelebrationVideo(result.next_video);
        }
      }
    } catch (error) {
      console.error('[WINDMILL] Error submitting answer:', error);
    }
  };

  const continueCaseStudy = () => {
    setShowFeedback(false);
    
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    
    if (nextStep < steps.length) {
      const step = steps[nextStep];
      playVideo(step.src);
    } else {
      showCompletion();
    }
  };

  const showCompletion = () => {
    setShowQuestion(true);
    setCurrentQuestion({
      title: 'CASE STUDY COMPLETE',
      subtitle: 'Williams Windmill Analysis Finished',
      question: 'You have completed the interactive case study on the Williams windmill project. The decisions and calculations you made demonstrate the complex engineering considerations involved in sustainable energy solutions for rural communities.',
      isComplete: true
    });
  };

  if (loading) {
    return (
      <div className="windmill-game-page">
        <div className="windmill-game-loading">
          <div className="loading-spinner"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="windmill-game-page">
        <div className="windmill-game-error">
          <p>App not found</p>
          <button onClick={() => navigate(parentAppId ? `/apps-store/${parentAppId}/experience` : `/apps-store/${id}`)} className="btn-back-to-detail">
            {parentAppId ? 'Back to Case' : 'Back to App Details'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="windmill-game-page">
      {/* Header */}
      <header className="windmill-game-header">
        <div className="windmill-game-header-content">
          <div className="windmill-game-left-section">
            <button 
              className="back-button"
              onClick={() => (parentAppId ? navigate(`/apps-store/${parentAppId}/experience`) : navigate('/apps'))}
              aria-label="Back to Apps"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="windmill-game-logo">
              <Logo_black onClick={() => navigate('/apps')} />
            </div>
            <h1 className="windmill-game-title">{app.title}</h1>
          </div>
          
          <div className="windmill-game-header-actions" />
        </div>
      </header>

      {/* Game Content */}
      <main className="windmill-game-main">
        <div className="windmill-video-container">
          <video 
            ref={videoRef}
            className="windmill-main-video"
            autoPlay
            muted
            playsInline
          >
            <source src={steps[0].src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Question Overlay */}
          {showQuestion && currentQuestion && (
            <div className="windmill-overlay active">
              <div className="windmill-question-panel">
                {currentQuestion.isComplete ? (
                  <>
                    <div className="windmill-question-header">
                      <div className="windmill-question-title">{currentQuestion.title}</div>
                      <div className="windmill-question-subtitle">{currentQuestion.subtitle}</div>
                    </div>
                    <div className="windmill-question-text">{currentQuestion.question}</div>
                    <div style={{ textAlign: 'center' }}>
                      <button className="windmill-continue-btn" onClick={() => (onComplete ? onComplete() : navigate(parentAppId ? `/apps-store/${parentAppId}/experience` : `/apps-store/${id}`))}>
                        {onComplete ? 'Continue' : 'Back to App Details'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="windmill-question-header">
                      <div className="windmill-question-title">{currentQuestion.title}</div>
                      <div className="windmill-question-subtitle">{currentQuestion.subtitle}</div>
                    </div>
                    <div className="windmill-question-text">{currentQuestion.question}</div>
                    <div className="windmill-options">
                      {Object.keys(currentQuestion.options || {}).map((key) => {
                        const option = currentQuestion.options[key];
                        return (
                          <div 
                            key={key}
                            className="windmill-option-button"
                            onClick={() => selectOption(key)}
                          >
                            <span className="windmill-option-letter">{key}</span>
                            <div className="windmill-option-title">{option.text}</div>
                            <div className="windmill-option-desc">{option.desc}</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Feedback Overlay */}
          {showFeedback && feedback && (
            <div className={`windmill-feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
              <div className="windmill-feedback-title">
                {feedback.correct ? 'EXCELLENT ANALYSIS' : 'THOUGHTFUL APPROACH'}
              </div>
              <div className="windmill-feedback-text">
                {feedback.explanation}
                {feedback.calculation && (
                  <>
                    <br /><br />
                    <strong>Calculation:</strong><br />
                    {feedback.calculation.split('\\n').map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </>
                )}
              </div>
              <button className="windmill-continue-btn" onClick={continueCaseStudy}>
                Continue
              </button>
            </div>
          )}

          {/* Loading Indicator */}
          {videoLoading && (
            <div className="windmill-loading">Loading...</div>
          )}

          {/* Progress Bar */}
          <div className="windmill-progress-bar">
            <div ref={progressFillRef} className="windmill-progress-fill"></div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WindmillGame;
